/**
 * "…was it there?" — Yes or No, for one place.
 *
 * ─── WHY A VERDICT AND NOT JUST A COUNT ───
 * The count was always available; what was missing was the answer. An operator asking "was the
 * red shirt person in the server room" reads "0 detections" as a technical outcome and has to
 * decide for themselves what it means, and an operator in a hurry reads a small number as a yes.
 * Stating the verdict in a word, then the evidence under it, is the difference between a report
 * and an answer.
 *
 * ─── WHAT "NO" IS ALLOWED TO MEAN ───
 * A No is only honest if the query could have found a Yes. Two cases where it could not, and both
 * suppress the verdict rather than reporting it:
 *   · nowhere was selected — a yes/no about the whole estate is not the question that was asked
 *   · no camera in the chosen place is visible to this user (`camera_scope === 0`) — "not there"
 *     and "you cannot see there" are completely different facts
 * The second is the one that would otherwise be actively misleading, and it is exactly what
 * `camera_scope` was added for.
 */
import type { ReactNode } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { SearchPlaceRow, SearchResult } from '../../../api/contracts';
import { AnswerShell, Caveat, Figure, fmtDateTime, type PlaceNamer } from './common';

export interface VerdictAnswerProps {
  result: SearchResult;
  namePlace: PlaceNamer;
  /** The place as the operator chose it, in words — "Server Room", "3 areas". */
  placeLabel: string;
  /** False when nothing is selected; the panel then asks for a place instead of answering. */
  hasPlace: boolean;
  /** The question in words, e.g. "a person in a red top" — built by the page from the filters. */
  subjectPhrase: string;
  /** The matching detections, rendered under the verdict as the evidence. */
  evidence?: ReactNode;
}

export default function VerdictAnswer({
  result, namePlace, placeLabel, hasPlace, subjectPhrase, evidence,
}: VerdictAnswerProps) {
  if (!hasPlace) {
    return (
      <div className="p-8 text-center">
        <ExclamationTriangleIcon className="mx-auto h-6 w-6 text-amber-500" />
        <p className="mt-2 text-sm font-medium text-foreground">Pick a place first</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
          A Yes or No needs somewhere to be about. Choose a camera or an area above, and this
          becomes “was {subjectPhrase} there?”. Until then the other questions still work —
          “Which areas?” will tell you everywhere it happened.
        </p>
      </div>
    );
  }

  if (result.camera_scope === 0) {
    return (
      <div className="p-8 text-center">
        <ExclamationTriangleIcon className="mx-auto h-6 w-6 text-amber-500" />
        <p className="mt-2 text-sm font-medium text-foreground">Cannot answer for {placeLabel}</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
          No camera there is visible to you, so this search had nothing to look at. That is not the
          same as “it was not there”, and it would be wrong to report a No.
        </p>
      </div>
    );
  }

  const rows = (result.rows || []) as SearchPlaceRow[];
  const events = rows.reduce((n, r) => n + r.events, 0);
  const yes = events > 0;

  const firstSeen = rows.reduce<string | null>(
    (min, r) => (!min || r.first_seen < min ? r.first_seen : min), null,
  );
  const lastSeen = rows.reduce<string | null>(
    (max, r) => (!max || r.last_seen > max ? r.last_seen : max), null,
  );
  const people = result.subjects_omitted
    ? null
    : rows.reduce((n, r) => n + (r.subjects || 0), 0);

  return (
    <AnswerShell
      headline={(
        <span className={`flex items-center gap-2 ${yes ? 'text-emerald-500' : 'text-foreground'}`}>
          {yes
            ? <CheckCircleIcon className="h-6 w-6" />
            : <XCircleIcon className="h-6 w-6 text-muted-foreground" />}
          <span className="text-xl font-semibold">{yes ? 'Yes' : 'No'}</span>
          <span className="text-sm font-normal text-muted-foreground">
            — {subjectPhrase} {yes ? 'was seen in' : 'was not seen in'} {placeLabel}
          </span>
        </span>
      )}
      sub={yes
        ? `First at ${fmtDateTime(firstSeen)}, last at ${fmtDateTime(lastSeen)}`
        : undefined}
      figures={yes ? (
        <>
          <Figure value={events} label={events === 1 ? 'detection' : 'detections'} tone="good" />
          {people !== null && (
            <Figure value={people} label={people === 1 ? 'distinct person' : 'distinct people'} />
          )}
          <Figure
            value={rows.length}
            label={rows.length === 1 ? 'camera saw it' : 'cameras saw it'}
          />
        </>
      ) : undefined}
      caveats={(
        <>
          <Caveat>
            Answered over the selected window only — a No means “not in this window”, not “never”.
          </Caveat>
          {typeof result.camera_scope === 'number' && (
            <Caveat>
              Checked {result.camera_scope} {result.camera_scope === 1 ? 'camera' : 'cameras'} you
              have access to. A camera you cannot see is not part of this answer.
            </Caveat>
          )}
        </>
      )}
    >
      {yes && rows.length > 0 && (
        <div className="rounded-md border border-border bg-card p-1.5">
          <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Where, exactly
          </div>
          {rows.map((r) => (
            <div key={r.place} className="flex items-center gap-3 px-2 py-1 text-xs">
              <span className="min-w-0 flex-1 truncate text-foreground">
                {namePlace(r.place, 'camera')}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {r.events} {r.events === 1 ? 'detection' : 'detections'}
              </span>
              <span className="w-32 shrink-0 text-right tabular-nums text-muted-foreground">
                {fmtDateTime(r.first_seen)}
              </span>
            </div>
          ))}
        </div>
      )}

      {evidence}
    </AnswerShell>
  );
}
